import { makeStyles, mergeClasses } from '@fluentui/react-components';
import { Delete, Sync } from '@material-symbols-svg/react/rounded/w500';

import { formatHM } from '../../common/format';
import { MONO_FONT, palette, tokens } from '../../theme';
import { workItemTypeAccent } from '../../work-items';
import type { PendingEntry } from '../../worklog/models';
import type { DayCell } from '../models';

export type DayCellEventsProps = {
    savedEntries: DayCell['savedEntries'];
    pendingAdds: PendingEntry[];
    removalByKey: ReadonlySet<string>;
};

export const DayCellEvents: React.FC<DayCellEventsProps> = ({ savedEntries, pendingAdds, removalByKey }) => {
    const classes = useStyles();

    if (savedEntries.length === 0 && pendingAdds.length === 0) {
        return null;
    }

    return (
        <div className={classes.events}>
            {savedEntries.map((entry, idx) => {
                const markedForRemoval = removalByKey.has(`${entry.issueId}|${entry.hours}`);
                return (
                    <div key={`logged-${entry.issueId}-${idx}`} className={classes.eventRow}>
                        <span
                            className={classes.eventDot}
                            style={{ backgroundColor: workItemTypeAccent(entry.workItemType, entry.issueTitle) }}
                        />
                        <span className={classes.eventTitle}>
                            <span className={classes.eventItemId}>#{entry.issueId}</span> {entry.issueTitle}
                        </span>
                        {markedForRemoval && <Delete className={classes.removeIcon} aria-label="Pending removal" />}
                        <span className={mergeClasses(classes.eventHours, markedForRemoval && classes.eventHoursStruck)}>
                            {formatHM(entry.hours)}
                        </span>
                    </div>
                );
            })}
            {pendingAdds.map((entry) => (
                <div key={`pending-${entry.id}`} className={classes.eventRow}>
                    <span
                        className={classes.eventDot}
                        style={{ backgroundColor: workItemTypeAccent(entry.workItemType, entry.workItemTitle) }}
                    />
                    <span className={classes.eventTitle}>
                        <span className={classes.eventItemId}>#{entry.workItemId}</span> {entry.workItemTitle}
                    </span>
                    <Sync className={classes.pendingIcon} aria-label="Pending" />
                    <span className={classes.eventHours}>{formatHM(entry.hours)}</span>
                </div>
            ))}
        </div>
    );
};

const useStyles = makeStyles({
    events: {
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
        flexGrow: 1,
        minHeight: 0,
        overflowY: 'auto',
        marginTop: '4px',
    },
    eventRow: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        padding: '2px 8px',
        borderRadius: '5px',
        backgroundColor: palette.panelMuted,
        flexShrink: 0,
        minHeight: '36px',
    },
    eventDot: {
        width: '7px',
        height: '7px',
        borderRadius: '50%',
        flexShrink: 0,
    },
    pendingIcon: {
        width: '12px',
        height: '12px',
        flexShrink: 0,
        color: palette.pendingAccent,
    },
    removeIcon: {
        width: '12px',
        height: '12px',
        flexShrink: 0,
        color: palette.overText,
    },
    eventTitle: {
        flex: 1,
        fontSize: '12px',
        fontWeight: tokens.fontWeightSemibold,
        color: tokens.colorNeutralForeground1,
        lineHeight: 1.15,
        display: '-webkit-box',
        WebkitBoxOrient: 'vertical',
        WebkitLineClamp: 2,
        overflow: 'hidden',
        minWidth: 0,
    },
    eventItemId: {
        fontFamily: MONO_FONT,
        fontVariantNumeric: 'tabular-nums',
        color: tokens.colorNeutralForeground3,
        fontWeight: tokens.fontWeightSemibold,
    },
    eventHours: {
        fontFamily: MONO_FONT,
        fontSize: '12px',
        fontWeight: tokens.fontWeightSemibold,
        color: tokens.colorNeutralForeground2,
        flexShrink: 0,
    },
    eventHoursStruck: {
        textDecoration: 'line-through',
        color: tokens.colorNeutralForeground3,
    },
});
